import { useLocation } from '@gatsbyjs/reach-router';
import { type ReactNode, useEffect } from 'react';
import { connect } from 'react-redux';
import { useConfiguration } from '@printdeal/configuration/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { VatModeProvider } from '@printdeal/product-selector';
import { ConsentBanner, useConsent } from '@printdeal/consent-management/react';
import useTranslations from '../hooks/useTranslations';
import { ServiceFactory } from '../services/ServiceFactory';
import { actions as authActions } from '../store/slices/authSlice';
import { IPageContext, Locale } from '../types/gatsby';
import ErrorBoundary from './error-handling/ErrorBoundary';
import IntlProvider from './IntlProvider';
import OrganizationStructuredData from './Organisation';
import { FeatureManagementProvider } from './feature-management';
import Chat from './widgets/chat/Chat';
import { LandingPageScript } from './webgain/LandingPageScript';
import ClientOnly from './ClientOnly';
import ChatButton from './widgets/chat/ChatButton';
import { getLanguageFromLocale } from '../helpers/translations';
import VatTogglePreferenceSync from './navigation/header/usp-bar/VatTogglePreferenceSync';

interface Props {
  pageContext: IPageContext;
  children: ReactNode;
  authenticate: () => void;
}

const PageElementWrapper = ({ pageContext, children, authenticate }: Props) => {
  // Authenticate on every location change
  const location = useLocation();
  const consent = useConsent();

  useEffect(() => {
    authenticate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const { defaultLocale, locales } = useConfiguration();

  let localeFromPage = pageContext.locale || defaultLocale;

  useEffect(() => {
    const itpService = ServiceFactory.createItpService();
    itpService.getItp(localeFromPage);
  }, [localeFromPage, consent.consented, consent.declined]);

  // In case of a 404, the locale isn't set in pageContext.
  // We'll try to see if the first section of the pathname matches
  // any of our configured locales.
  //
  // Example 404 url with locale: `/fr/asdf`
  // -------------------------------^^
  //
  // Example 404 without locale: `/asdf`
  //
  // If so: we'll set that as the locale.
  // If not: use the default locale.
  if (!pageContext.locale) {
    // The firstSlug *possibly* contains a locale
    const [, firstSlug] = location.pathname.split('/');

    // => ['nl-BE', 'fr-BE'] -> ['nl', 'fr']
    const localeSlugs = locales.map((localeCode) => localeCode.slice(0, 2));
    const localeIndex = localeSlugs.indexOf(firstSlug);
    const supportsLocale = localeSlugs.includes(firstSlug);

    if (supportsLocale) {
      localeFromPage = locales[localeIndex] as Locale;
    }
  }
  const messages = useTranslations(localeFromPage);

  // Provide intl at page level, because we need the locale from page context
  return (
    <>
      {/* This element is used in Playwright tests to determine if the page is hydrated and therefore
      ready for interactions, like hover and focus */}
      <ClientOnly>
        <meta name="hydration" content="true" />
      </ClientOnly>

      <IntlProvider localeFromPage={localeFromPage} messages={messages}>
        <FeatureManagementProvider>
          <VatModeProvider>
            <VatTogglePreferenceSync />
            <Chat>
              <ErrorBoundary>
                {children}
              </ErrorBoundary>
              <ErrorBoundary inline>
                {!['uploader'].includes(pageContext.template || '') && (
                  <ClientOnly>
                    <ChatButton />
                  </ClientOnly>
                )}
                <OrganizationStructuredData />
              </ErrorBoundary>
            </Chat>
          </VatModeProvider>
        </FeatureManagementProvider>
        <ErrorBoundary inline>
          <ConsentBanner language={getLanguageFromLocale(localeFromPage)} />
        </ErrorBoundary>
      </IntlProvider>
      <LandingPageScript />
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
    </>
  );
};

const mapActionsToProps = {
  authenticate: authActions.authenticate,
};

const ConnectedPageElementWrapper = connect(null, mapActionsToProps)(PageElementWrapper);

// This wrapper is needed because of the Gatsby browser API
// eslint-disable-next-line react/function-component-definition, func-names
export default function ({ element, props }: { element: ReactNode; props: Props }) {
  return <ConnectedPageElementWrapper {...props}>{element}</ConnectedPageElementWrapper>;
}
